import {useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';
import {useUpdateEntryManagement} from '../../../core/repositories/mypageEntryManagementsRepository';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../shared/components';

/**
 * エラーメッセージ定数
 */
export const ERROR_MESSAGES = {
  OVERALL_STATUS_UPDATE: {
    title: '更新失敗',
    message: 'ステータスの更新に失敗しました',
  },
} as const;

/**
 * 成功メッセージ定数
 */
export const SUCCESS_MESSAGES = {
  OVERALL_STATUS_UPDATE: {
    title: '更新完了',
    message: 'ステータスを更新しました',
  },
} as const;

/**
 * キャッシュキー定数
 */
export const CACHE_KEYS = {
  PAGINATED_ENTRY_MANAGEMENTS: 'paginatedEntryManagements',
  ENTRY_MANAGEMENTS: 'entryManagements',
  ENTRY_MANAGEMENT: 'entryManagement',
  ENTRY_MANAGEMENT_REMINDERS: 'entryManagementReminders',
  PROGRESS_MISSIONS: 'progressMissions',
  ENTRY_CALENDARS: 'entryCalendars',
} as const;

type UseEntryManagementUpdateArgs = {
  entryManagementId: number;
  companyId: number;
  companyName: string;
  showSuccessMessage?: boolean;
};

/**
 * エントリー管理の更新とキャッシュ無効化を共通化したカスタムフック
 */
export const useEntryManagementUpdate = ({
  entryManagementId,
  companyId,
  companyName,
  showSuccessMessage = true,
}: UseEntryManagementUpdateArgs) => {
  const updateMutation = useUpdateEntryManagement();
  const queryClient = useQueryClient();

  /**
   * エントリー管理のキャッシュを無効化する
   */
  const invalidateEntryManagementCache = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.PAGINATED_ENTRY_MANAGEMENTS],
    });
    await queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.ENTRY_MANAGEMENTS],
    });
    await queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.ENTRY_MANAGEMENT, entryManagementId],
    });
    await queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.ENTRY_MANAGEMENT_REMINDERS],
    });
    await queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.PROGRESS_MISSIONS],
    });
  }, [queryClient, entryManagementId]);

  /**
   * overallStatusを更新する
   */
  const updateOverallStatus = useCallback(
    async (overallStatus: string) => {
      try {
        await updateMutation.mutateAsync({
          id: entryManagementId,
          companyID: companyId,
          companyName,
          overallStatus,
        });

        await invalidateEntryManagementCache();

        if (showSuccessMessage) {
          showSuccessSnackBar(SUCCESS_MESSAGES.OVERALL_STATUS_UPDATE);
        }
      } catch (error) {
        console.error('overallStatus更新エラー:', error);
        showErrorSnackBar(ERROR_MESSAGES.OVERALL_STATUS_UPDATE);
        throw error;
      }
    },
    [
      updateMutation,
      entryManagementId,
      companyId,
      companyName,
      invalidateEntryManagementCache,
      showSuccessMessage,
    ],
  );

  return {
    updateOverallStatus,
    invalidateEntryManagementCache,
  };
};
